import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler, Controller, useWatch } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { ValidationUtils } from "../../../../../Utils";
import { FmTextField, FmAutocompleteInput, FmText, FmMessage, FmIcon, FmCheckBox, FmButton, FmLoader, FmTermsPopup } from "../../../../../Components";
import { useTheme, useIsMobile } from "../../../../../Hooks";
import AuthState from "../../../../../Redux/States/Auth";
import useStyles from "./RegisterForm.style";

interface IFormInputs {
  fullName: string;
  country: string;
  region: string | undefined;
  email: string;
  mainInterest: string;
  expertise: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
  emailResendInProgress: boolean;
}

interface RegisterFormProps {
  onSubmit: (
    fullName: string,
    country: string,
    region: string | undefined,
    email: string,
    mainInterest: string,
    expertise: string,
    password: string,
    acceptTerms: boolean,
  ) => void;
  submitted: boolean;
  submittedEmail?: string;
  submitInProgress: boolean;
  error?: string;
  resendEmail?: () => void;
  emailResendInProgress: boolean;
}

const RegisterForm = ({ onSubmit, submitted, submittedEmail, submitInProgress, error, resendEmail, emailResendInProgress }: RegisterFormProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useIsMobile();
  const theme = useTheme();

  const { fullName, email, password, confirmPassword, acceptTerms } = useSelector(AuthState.Selectors.selectRegisterFormData);
  const { countries, regionsByCountry, specializations, mainInterests } = useSelector(AuthState.Selectors.selectRegistrationData);
  const {
    handleSubmit,
    control,
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<IFormInputs>({
    mode: "onBlur",
    defaultValues: {
      fullName,
      email,
      password,
      confirmPassword,
      acceptTerms,
    },
  });
  // const formResult = useWatch({ control });
  // useEffect(() => {
  //   const { fullName, email, password, confirmPassword, acceptTerms } =
  //     formResult;
  //   // dispatch(AuthState.Actions.setCountry(country));
  // }, [formResult]);
  const [toggleCountryUpdate, setToggleCountryUpdate] = useState<boolean>(false);
  const [passwordIsVisible, setPasswordIsVisible] = useState<boolean>(false);
  const [mobileFirstPartIsActive, setMobileFirstPartIsActive] = useState<boolean>(true);
  const [confirmPasswordIsVisible, setConfirmPasswordIsVisible] = useState<boolean>(false);
  const [showTerms, setShowTerms] = useState<boolean>(false);
  const [showPrivacy, setShowPrivacy] = useState<boolean>(false);

  const regions = useMemo(() => {
    const country = getValues("country");

    const countriesRegions = regionsByCountry[country];
    return countriesRegions || [];
  }, [regionsByCountry, toggleCountryUpdate, getValues]);

  const handleOnSubmit: SubmitHandler<IFormInputs> = (data) => {
    if (!onSubmit) return;

    onSubmit(
      data.fullName.trim(),
      data.country.trim(),
      data.region?.trim(),
      data.email.trim(),
      data.mainInterest.trim(),
      data.expertise.trim(),
      data.password,
      !!data.acceptTerms,
    );
  };

  const handleOnFailure = () => setMobileFirstPartIsActive(true);

  useEffect(() => {
    if (isMobile && !mobileFirstPartIsActive) {
      return history.listen(() => {
        if (history.action === "POP") {
          setMobileFirstPartIsActive(true);
          history.go(1);
        }
      });
    }
  }, [history, isMobile, mobileFirstPartIsActive]);

  useEffect(() => {
    return () => {
      dispatch(
        AuthState.Actions.setRegisterFormData({
          fullName: getValues("fullName"),
          country: getValues("country"),
          region: getValues("region"),
          email: getValues("email"),
          mainInterest: getValues("mainInterest"),
          expertise: getValues("expertise"),
          password: getValues("password"),
          confirmPassword: getValues("confirmPassword"),
          acceptTerms: getValues("acceptTerms"),
        }),
      );
    };
  }, [dispatch]);

  const handleResendEmail = () => resendEmail && resendEmail();
  const showFirstPart = !isMobile || mobileFirstPartIsActive;
  const showSecondPart = !isMobile || !mobileFirstPartIsActive;
  const showNextButton = showFirstPart && isMobile;
  const showErrorMessage = !submitInProgress && !!error;
  const showSuccessMessage = !submitInProgress && submitted && !showErrorMessage;

  const checkBoxState = getValues("acceptTerms");

  const isCheckHandler = () => {
    return !!getValues("acceptTerms");
  };
  return (
    <form onSubmit={handleSubmit(handleOnSubmit, handleOnFailure)} className={classes.container}>
      <FmText className={classes.note}>{t("signUp.note")}</FmText>
      {showFirstPart && (
        <div className={classes.upperContainer}>
          <Controller
            name={"fullName"}
            control={control}
            rules={{
              required: {
                value: true,
                message: t("validationErrors.fieldIsRequired"),
              },
              pattern: {
                value: ValidationUtils.StringRegex.LETTERSONLY,
                message: t("validationErrors.lettersOnly"),
              },
            }}
            render={({ field }) => {
              return (
                <FmTextField
                  {...register("fullName")}
                  {...field}
                  label={t("signUp.fullName")}
                  variant="filled"
                  error={errors.fullName}
                  value={field.value}
                  helperText={errors.fullName?.message || ""}
                />
              );
            }}
          />
          <Controller
            name={"country"}
            control={control}
            rules={{
              required: {
                value: true,
                message: t("validationErrors.fieldIsRequired"),
              },
            }}
            render={({ field }) => {
              return (
                <FmAutocompleteInput
                  {...register("country")}
                  {...field}
                  label={t("signUp.country")}
                  error={errors.country}
                  helperText={errors.country?.message || ""}
                  data={countries}
                  getLabel={(country: any) => t(`countries.${country.code}`)}
                  setValue={(country: any) => {
                    setToggleCountryUpdate((update) => !update);
                    setValue("region", undefined);
                    setValue("country", country && country.code ? country.code : undefined);
                    // dispatch(
                    //   AuthState.Actions.setCountry(
                    //     country && country.code ? country.code : '',
                    //   ),
                    // );
                  }}
                />
              );
            }}
          />
          {regions && regions.length > 0 && (
            <Controller
              name={"region"}
              control={control}
              key={regions && regions[0].countryCode}
              render={({ field }) => {
                return (
                  <FmAutocompleteInput
                    {...register("region")}
                    {...field}
                    label={t("signUp.region")}
                    error={errors.region}
                    helperText={errors.region?.message || ""}
                    data={regions}
                    getLabel={(region: any) => t(`regions.${region.code}`)}
                    setValue={(region: any) => {
                      setValue("region", region && region.code ? region.code : undefined);
                    }}
                  />
                );
              }}
            />
          )}
          <Controller
            name={"email"}
            control={control}
            rules={{
              required: {
                value: true,
                message: t("validationErrors.fieldIsRequired"),
              },
              pattern: {
                value: ValidationUtils.StringRegex.EMAIL,
                message: t("validationErrors.incorrectEmailFormat"),
              },
            }}
            render={({ field }) => {
              return (
                <FmTextField
                  {...register("email")}
                  {...field}
                  label={t("signIn.email")}
                  variant="filled"
                  error={errors.email}
                  value={field.value}
                  helperText={errors.email?.message || ""}
                />
              );
            }}
          />

          <Controller
            name={"mainInterest"}
            control={control}
            rules={{
              required: {
                value: true,
                message: t("validationErrors.fieldIsRequired"),
              },
            }}
            render={({ field }) => {
              return (
                <FmAutocompleteInput
                  {...register("mainInterest")}
                  {...field}
                  label={t("signUp.mainInterest")}
                  error={errors.mainInterest}
                  helperText={errors.mainInterest?.message || ""}
                  data={mainInterests}
                  getLabel={(mainInterest: any) => t(`mainInterests.${mainInterest.code}`)}
                  setValue={(mainInterest: any) => {
                    setValue("mainInterest", mainInterest && mainInterest.code ? mainInterest.code : undefined);
                  }}
                />
              );
            }}
          />
          <Controller
            name={"expertise"}
            control={control}
            rules={{
              required: {
                value: true,
                message: t("validationErrors.fieldIsRequired"),
              },
            }}
            render={({ field }) => {
              return (
                <FmAutocompleteInput
                  {...register("expertise")}
                  {...field}
                  label={t("signUp.expertise")}
                  error={errors.expertise}
                  helperText={errors.expertise?.message || ""}
                  data={specializations}
                  getLabel={(expertise: any) => t(`specialties.${expertise.code}`)}
                  setValue={(expertise: any) => {
                    setValue("expertise", expertise && expertise.code ? expertise.code : undefined);
                  }}
                />
              );
            }}
          />
        </div>
      )}
      {showNextButton && (
        <div className={classes.nextButton}>
          <FmButton fullWidth="mobileFullWidth" onClick={() => setMobileFirstPartIsActive(false)}>
            {t("signUp.next")}
          </FmButton>
        </div>
      )}
      {showSecondPart && (
        <div className={classes.bottomContainer}>
          <FmText className={classes.note}>{t("signUp.passwordNote")}</FmText>
          <div className={classes.bottomFormContainer}>
            <Controller
              name={"password"}
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("validationErrors.fieldIsRequired"),
                },
                minLength: {
                  value: 8,
                  message: t("validationErrors.passwordValidation"),
                },
                pattern: {
                  value: ValidationUtils.StringRegex.PASSWORD,
                  message: t("validationErrors.passwordValidation"),
                },
              }}
              render={({ field }) => {
                return (
                  <FmTextField
                    {...register("password")}
                    {...field}
                    label={t("signUp.password")}
                    variant="filled"
                    type={passwordIsVisible ? "text" : "password"}
                    error={errors.password}
                    value={field.value}
                    displayIcon={true}
                    onIconClick={() => setPasswordIsVisible((visible: boolean) => !visible)}
                    inputProps={{
                      icon: <FmIcon url={theme.images.eye} title={"visible"} />,
                    }}
                    helperText={errors.password?.message || ""}
                  />
                );
              }}
            />
            <Controller
              name={"confirmPassword"}
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("validationErrors.fieldIsRequired"),
                },
                validate: ((value: string) => (value !== watch("password") ? t("validationErrors.passwordsDontMatch") : undefined)) as any,
              }}
              render={({ field }) => {
                return (
                  <FmTextField
                    {...register("confirmPassword")}
                    {...field}
                    label={t("signUp.confirmPassword")}
                    variant="filled"
                    type={confirmPasswordIsVisible ? "text" : "password"}
                    error={errors.confirmPassword}
                    value={field.value}
                    displayIcon={true}
                    onIconClick={() => setConfirmPasswordIsVisible((visible: boolean) => !visible)}
                    inputProps={{
                      icon: <FmIcon url={theme.images.eye} title={"visible"} />,
                    }}
                    helperText={errors.confirmPassword?.message || ""}
                  />
                );
              }}
            />
          </div>
          <div className={classes.terms}>
            <div className={classes.checkboxContainer}>
              <FmCheckBox
                name="acceptTerms"
                control={control}
                register={register}
                isChecked={isCheckHandler}
                required={true}
                helpertext={errors.acceptTerms?.message || ""}
              />
            </div>
            <FmText>
              {`${t("signUp.iAccept")} `}
              <span className={classes.clickableSpan} onClick={() => setShowTerms(true)}>{`${t("signUp.termsAndConditions")} `}</span>
              {`${t("signUp.and")} `}
              <span className={classes.clickableSpan} onClick={() => setShowPrivacy(true)}>{`${t("signUp.privacyPolicy")} `}</span>
            </FmText>
          </div>
          {!checkBoxState && <span className={classes.checkBoxError}>{t("validationErrors.termsCheckbox")}</span>}
          {showErrorMessage && (
            <FmMessage className={classes.message} variant={"failure"}>
              {error}
            </FmMessage>
          )}
          {showSuccessMessage && (
            <FmMessage className={classes.message} variant={"success"}>
              {emailResendInProgress && (
                <div className={classes.loaderContainer}>
                  <FmLoader size={"verySmall"} />
                </div>
              )}
              {!emailResendInProgress && (
                <>
                  {t("signUp.emailSent").replace("{0}", submittedEmail || "")}
                  <FmText onClick={handleResendEmail} className={classes.resend}>
                    {t("signUp.didntGet")}
                  </FmText>
                </>
              )}
            </FmMessage>
          )}
          <div className={classes.buttonContainer}>
            <FmButton loading={submitInProgress} type="submit" fullWidth="mobileFullWidth" disabled={submitInProgress}>
              {t("authorizationTabs.signUp")}
            </FmButton>
          </div>
          <div className={classes.termsAndPrivacy_container}>
            <FmTermsPopup
              title={t("sideMenu.termsAndCondition.contentTitle")}
              description={t("sideMenu.termsAndCondition.data")}
              isShow={showTerms}
              setIsShow={setShowTerms}
            />
          </div>
          <div className={classes.termsAndPrivacy_container}>
            <FmTermsPopup
              title={t("sideMenu.termsAndCondition.title")}
              description={t("sideMenu.termsAndCondition.data")}
              isShow={showPrivacy}
              setIsShow={setShowPrivacy}
            />
          </div>
        </div>
      )}
    </form>
  );
};

export default RegisterForm;
